/*
 * product-modal.js
 * ----------------------------------------------------------------------------
 * Erstatter Crafts indbyggede billed-lightbox med PhotoSwipe 5.
 *
 * Hvorfor: temaets egen modal viser ét billede ad gangen, har ingen swipe,
 * og lukkeknappen scroller ud af skaermen naar billedet er bredere end viewporten.
 *
 * Hvordan: <modal-opener> kalder modal.show(knap). Vi overskriver show() og
 * kalder IKKE super.show(), saa den gamle modal aabner aldrig. Billederne
 * hentes fra modalens eget markup, saa product-media.liquid er uaendret.
 *
 * Video og 3D-modeller falder automatisk tilbage til temaets modal.
 *
 * Kraever: snippets/photoswipe.liquid renderet i theme.liquid.
 */

if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.pswpLightbox = null;
        this.pswpOpener = null;
        this.pswpLoading = false;
      }

      hide() {
        super.hide();
      }

      show(opener) {
        const mediaId = opener ? opener.getAttribute('data-media-id') : null;
        const items = this.buildPhotoSwipeItems();
        const index = items.findIndex((item) => item.mediaId === mediaId);

        // Ikke et billede (video/3D), eller assets mangler -> temaets egen modal
        if (index === -1 || !this.photoSwipeUrls()) {
          super.show(opener);
          this.showActiveMedia();
          return;
        }

        this.openPhotoSwipe(items, index, opener);
      }

      /* --------------------------------------------------------------------
       * PhotoSwipe
       * ------------------------------------------------------------------ */

      photoSwipeUrls() {
        const assets = window.themeAssets;
        if (!assets || !assets.photoswipe || !assets.photoswipeLightbox) return null;
        return assets;
      }

      buildPhotoSwipeItems() {
        return Array.from(this.querySelectorAll('img[data-media-id]')).map((img) => {
          const largest = this.largestSource(img);

          const attrWidth = parseFloat(img.getAttribute('width')) || img.naturalWidth || 0;
          const attrHeight = parseFloat(img.getAttribute('height')) || img.naturalHeight || 0;
          const ratio = attrWidth && attrHeight ? attrHeight / attrWidth : 1;

          const width = largest.width || attrWidth || 1100;

          return {
            mediaId: img.getAttribute('data-media-id'),
            src: img.getAttribute('src') || largest.url,
            srcset: img.getAttribute('srcset') || undefined,
            msrc: this.thumbnailSrc(img.getAttribute('data-media-id')),
            width: Math.round(width),
            height: Math.round(width * ratio),
            alt: img.getAttribute('alt') || '',
          };
        });
      }

      // Stoerste kandidat i et srcset. Shopify returnerer protokol-relative
      // URL'er (//cdn.shopify.com/...), saa vi splitter kun paa kommaer der
      // efterfoelges af en URL - filnavne maa gerne indeholde komma.
      largestSource(img) {
        const srcset = img.getAttribute('srcset') || '';

        const candidates = srcset
          .split(/,(?=\s*(?:https?:)?\/\/)/)
          .map((part) => part.trim())
          .filter(Boolean)
          .map((part) => {
            const bits = part.split(/\s+/);
            return { url: bits[0], width: parseInt(bits[1], 10) || 0 };
          })
          .filter((candidate) => candidate.url);

        if (!candidates.length) {
          return { url: img.currentSrc || img.src, width: img.naturalWidth || 0 };
        }

        candidates.sort((a, b) => b.width - a.width);
        return candidates[0];
      }

      // Miniature fra galleriet, saa PhotoSwipe har noget at vise med det samme
      thumbnailSrc(mediaId) {
        if (!mediaId) return undefined;
        const thumb = document.querySelector(`.product__media-item[data-media-id="${mediaId}"] img`);
        return thumb ? thumb.currentSrc || thumb.src : undefined;
      }

      async openPhotoSwipe(items, index, opener) {
        if (this.pswpLoading) return;
        this.pswpOpener = opener;

        try {
          const lightbox = await this.getLightbox();
          lightbox.options.dataSource = items;
          lightbox.loadAndOpen(index);
        } catch (error) {
          console.error('[product-modal] PhotoSwipe kunne ikke indlaeses:', error);
          super.show(opener);
          this.showActiveMedia();
        }
      }

      async getLightbox() {
        if (this.pswpLightbox) return this.pswpLightbox;

        const urls = this.photoSwipeUrls();
        this.pswpLoading = true;

        try {
          const module = await import(urls.photoswipeLightbox);
          const PhotoSwipeLightbox = module.default;

          const lightbox = new PhotoSwipeLightbox({
            pswpModule: () => import(urls.photoswipe),

            bgOpacity: 1,
            showHideAnimationType: 'fade',
            padding: { top: 16, bottom: 16, left: 0, right: 0 },

            initialZoomLevel: 'fit',
            secondaryZoomLevel: 2,
            maxZoomLevel: 4,
            wheelToZoom: true,

            // Tilbage-knap og tilbage-swipe lukker lightboxen
            history: true,

            closeTitle: 'Luk',
            zoomTitle: 'Zoom ind/ud',
            arrowPrevTitle: 'Forrige billede',
            arrowNextTitle: 'Naeste billede',
            errorMsg: 'Billedet kunne ikke indlaeses',
          });

          lightbox.on('close', () => {
            if (this.pswpOpener && typeof this.pswpOpener.focus === 'function') {
              this.pswpOpener.focus();
            }
          });

          lightbox.init();
          this.pswpLightbox = lightbox;
          return lightbox;
        } finally {
          this.pswpLoading = false;
        }
      }

      /* --------------------------------------------------------------------
       * Temaets oprindelige modal - bruges nu kun som fallback
       * ------------------------------------------------------------------ */

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }
    }
  );
}
